import type { Item, ItemType, LodgingDetails, Place } from './schema';
import { diffDays, localDate } from './time';

export type PlaceLookup = (id: string | null | undefined) => Place | undefined;

/** Plans that span nights and get a bracket on the timeline. */
export function isSpanType(type: ItemType): boolean {
  return type === 'lodging' || type === 'car';
}

/** Plans with a departure and an arrival. */
export function isMoveType(type: ItemType): boolean {
  return (
    type === 'flight' || type === 'train' || type === 'bus' || type === 'ferry' || type === 'cruise'
  );
}

export const ITEM_TYPE_LABELS: Record<ItemType, string> = {
  flight: 'Flight',
  lodging: 'Stay',
  car: 'Car rental',
  train: 'Train',
  bus: 'Bus',
  ferry: 'Ferry',
  cruise: 'Cruise',
  restaurant: 'Restaurant',
  activity: 'Activity',
  transfer: 'Ride',
  parking: 'Parking',
  other: 'Other',
};

export function startDateOf(item: Item): string | null {
  return item.startAt != null && item.startTz ? localDate(item.startAt, item.startTz) : null;
}

export function endDateOf(item: Item): string | null {
  if (item.endAt != null && item.endTz) return localDate(item.endAt, item.endTz);
  return startDateOf(item);
}

/** Nights in a stay or days of a car rental, by local dates. */
export function spanNights(item: Item): number {
  const s = startDateOf(item);
  const e = endDateOf(item);
  if (!s || !e) return 0;
  return Math.max(0, diffDays(s, e));
}

/** Rental length in days, counting partial days as a day. */
export function rentalDays(item: Item): number {
  if (item.startAt == null || item.endAt == null) return 0;
  return Math.max(1, Math.ceil((item.endAt - item.startAt) / 86_400_000 - 0.05));
}

/** How a stay is referred to in a sentence: 'the Airbnb', 'the Crowne Plaza', 'Casa Azul'. */
export function stayShortName(item: Item): string {
  if (item.details.type !== 'lodging') return item.title;
  const d = item.details as LodgingDetails;
  if (d.kind === 'airbnb') return 'the Airbnb';
  if (d.brand) return `the ${d.brand}`;
  return d.propertyName || item.title;
}

export function stayName(item: Item): string {
  if (item.details.type !== 'lodging') return item.title;
  return item.details.propertyName || item.title;
}

/** City where a plan happens (arrival city for moves). */
export function cityOf(item: Item, places: PlaceLookup): string | null {
  const p = isMoveType(item.type) ? places(item.endPlaceId) : places(item.startPlaceId);
  return p?.city ?? places(item.startPlaceId)?.city ?? null;
}

export function flightLabel(item: Item): string {
  if (item.details.type !== 'flight') return item.title;
  return `${item.details.airline} ${item.details.flightNumber}`.trim();
}

/** Sorts plans by start time; plans without a time go last. */
export function byStart(a: Item, b: Item): number {
  const as = a.startAt ?? Number.MAX_SAFE_INTEGER;
  const bs = b.startAt ?? Number.MAX_SAFE_INTEGER;
  return as - bs || a.id.localeCompare(b.id);
}
